<i18n>
{
  "de": {
    "toasts.followship.created.success.title": "Danke Dir!",
    "toasts.followship.created.success.message": "Du folgst nun {artist} und wirst über Neuigkeiten informiert.",
    "toasts.community.pinned.enabled.info.title": "Kommentar oben angeheftet",
    "toasts.community.pinned.enabled.info.message": "Der Kommentar wurde oben fixiert, damit er mehr Aufmerksamkeit erhält.",
    "toasts.community.pinned.disabled.info.title": "Kommentar abgelöst",
    "toasts.community.pinned.disabled.info.message": "Kommentar wurde wieder in den normalen Verlauf eingereiht.",
    "toasts.community.share.copied.success.title": "Link kopiert",
    "toasts.community.share.copied.success.message": "Der Link wurde in die Zwischenablage kopiert.",
    "toasts.community.share.copied.error.title": "Fehler",
    "toasts.community.share.copied.error.message": "Der Link konnte nicht in die Zwischenablage kopiert werden."
  },
  "en": {
    "toasts.followship.created.success.title": "Thank you!",
    "toasts.followship.created.success.message": "You are now following {artist}, and we will keep you updated on any news.",
    "toasts.community.pinned.enabled.info.title": "Comment pinned",
    "toasts.community.pinned.enabled.info.message": "The comment has been pinned at the top to give it more attention.",
    "toasts.community.pinned.disabled.info.title": "Comment detached",
    "toasts.community.pinned.disabled.info.message": "Comment was put back into the normal sequence.",
    "toasts.community.share.copied.success.title": "Link copied",
    "toasts.community.share.copied.success.message": "The link has been copied to the clipboard",
    "toasts.community.share.copied.error.title": "Copy failed",
    "toasts.community.share.copied.error.message": "The link could not be copied to the clipboard."
  }
}
</i18n>

<template>
  <div>
    <transition name="fade">
      <div
        v-show="profileLoading"
        class="profile"
        :style="{ backgroundColor: defaultBackgroundColor, position: 'fixed', width: '100%', height: '100vh' }"
      />
    </transition>
    <transition name="fade">
      <div
        v-show="!profileLoading"
        id="dynamic-color-scope"
        class="profile"
        :style="userColorStyles"
      >
        <div>
          <mainnav />
          <nuxt />
        </div>
        <no-ssr>
          <div v-if="isMobileView">
            <mobileNotificationControls
              v-if="artist && isAuthenticated"
              :user="artist"
            />
            <profile-social
              v-if="artist && activeSocials.length > 0"
              class="pt-10"
              :user="artist"
              :activeSocials="activeSocials"
            />
            <artistLegalLinks v-show="isMobileView" />
          </div>
        </no-ssr>
        <mainfooter ref="mainfooter" />
        <browserBanner />
        <no-ssr>
          <color-menu-button
            ref="colorMenuButton"
            :is-my-profile="myProfile"
            @color-menu-toggle="onColorMenuToggle"
          >
            <template #color-menu>
              <color-menu-content @saved="onColorMenuSaved" />
            </template>
          </color-menu-button>
          <FlashMessage :position="'right bottom'" />
          <floating-filter-button />
        </no-ssr>
      </div>
    </transition>
  </div>
</template>
<script>
import { mapState } from 'vuex'

import mainnav from '~/components/mainnav'
import mainfooter from '~/components/mainfooter'
import browserBanner from '~/components/browser-banner'
import colorMenuButton from '~/components/color-menu-button'
import colorMenuContent from '~/components/color-menu-content'
import artistLegalLinks from '~/components/artist-legal-links'
import floatingFilterButton from '~/components/floating-filter-button'
import mobileNotificationControls from '~/components/mobile-notification-controls'
import profileSocial from '~/components/profile-social'
import profileColorsMixin from '~/mixins/profileColors'

export default {
  components: {
    mainnav,
    mainfooter,
    browserBanner,
    colorMenuButton,
    colorMenuContent,
    artistLegalLinks,
    floatingFilterButton,
    mobileNotificationControls,
    profileSocial
  },
  head: () => ({
    bodyAttrs: {
      class: 'isProfile'
    }
  }),
  mixins: [profileColorsMixin],
  data: () => ({
    unscrollable: false
  }),
  computed: {
    ...mapState(['artist', 'user', 'isAuthenticated', 'profileLoading']),

    myProfile() {
      if (this.isAuthenticated) {
        return this.artist.nickname === this.user.nickname
      }
      return false
    },
    defaultBackgroundColor() {
      if (process.client && window && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return '#121212' // dark mode
      } else {
        return '#eeeeee' // light mode
      }
    },
    activeSocials() {
      return Object.keys(this.artist.socialAccounts).filter(
        k => this.artist.socialAccounts[k].enabled
      )
    }
  },
  methods: {
    scrollToTarget(target) {
      if (process.client && window) {
        window.history.scrollRestoration = 'manual'
      }

      const hashTarget = target && target.startsWith('#') ? target : `#${target}`
      if (!hashTarget) return

      try {
        const hashTargetElement = document.querySelector(hashTarget)
        this.$scrollTo(hashTargetElement, {
          duration: 800,
          force: true,
          cancelable: false
        })
      } catch (error) {
        console.error('Invalid hash:', hashTarget)
      }
    },
    toggleScrollable() {
      this.unscrollable = !this.unscrollable
      document.body.style.overflow = this.unscrollable ? 'hidden' : ''
      document.body.style.maxHeight = this.unscrollable ? '100vh' : ''
    },
    lockBodyForModal(enabled) {
      const windowScrollY = window.scrollY
      const documentBodyTop = document.body.style.top
      document.body.style.overflow = enabled ? 'hidden' : ''
      document.body.style.position = enabled ? 'fixed' : ''
      document.body.style.width = enabled ? '100%' : ''
      document.body.style.top = enabled ? `-${windowScrollY}px` : ''
      if (!enabled && documentBodyTop) {
        window.scrollTo(0, parseInt(documentBodyTop) * -1)
      }
    },
    resetScrollable() {
      document.body.style.overflow = ''
      document.body.style.maxHeight = ''
    },
    handleFlash({ type, state = 'success', flashConfig = {}, payload = {} }) {
      if (!type) return
      this.flashMessage[state]({
        title: this.$t(`${type}.${state}.title`, { ...payload }),
        message: this.$t(`${type}.${state}.message`, { ...payload })
      }, { ...flashConfig })
    },
    toggleFooterExpand() {
      // Communicate with mainfooter to toggle its expanded state
      if (this.$refs.mainfooter && this.$refs.mainfooter.toggleFooter) {
        this.$refs.mainfooter.toggleFooter()
      }
    },
    onColorMenuToggle(isOpen) {
      // Optional: handle color menu state changes if needed
      // Could be used to track analytics or lock body scroll
    },
    onColorMenuSaved() {
      // Color menu saved - close the menu
      this.$refs.colorMenuButton.closeColorMenu()
    }
  },
  mounted() {
    this.resetScrollable()
    this.$root.$on('lockBodyForModal', this.lockBodyForModal)
    this.$root.$on('toggleBodyScrollable', this.toggleScrollable)
    this.$root.$on('flash', this.handleFlash)

    this.$nextTick(() => {
      if (this.$route.hash) {
        return this.scrollToTarget(this.$route.hash)
      }
      if (this.$route.params.scrollTo && this.$route.params.scrollTo.target) {
        return this.scrollToTarget(this.$route.params.scrollTo.target)
      }
      if (this.$route.meta.scroll && this.$route.meta.scroll.target) {
        return this.scrollToTarget(this.$route.meta.scroll.target)
      }
    })
  },
  destroyed() {
    this.$root.$off('lockBodyForModal', this.lockBodyForModal)
    this.$root.$off('toggleBodyScrollable', this.toggleScrollable)
    this.$root.$off('flash', this.handleFlash)
  }
}
</script>
<style lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Shop page transitions - faster and smoother for filtering
.shop-page-enter-active,
.shop-page-leave-active {
  transition: opacity 0.15s ease;
}

.shop-page-enter,
.shop-page-leave-to {
  opacity: 0;
}
</style>
